firebird.factory('videoVolcomixFilterService', [
    '$window',
    '$rootScope',
    '$timeout',
    'logService',
    'helperService',
    'storageService',
    'configService',
    function ($window, $rootScope, $timeout, logService, helperService, storageService, configService) {
        const logger = logService.createLoggerWithSource('videoVolcomixFilterService');

        // === PUBLIC SERVICE INTERFACE ===
        // Provides video filtering capabilities with ML-based background processing
        var service = {
            initialize: initialize,
            // === MODEL MANAGEMENT ===
            isActive: isActive, // Check if a supported model is currently active.
            isSupported: isSupported, // Check if the supplied model name is supported by this service.
            setModel: setModel, // Configure active ML model (mlkit, meet, meet-lite, mediapipe-v2)
            getModel: getModel, // Get the currently configured model

            // === FILTER LIFECYCLE ===
            initializeFilterDefaults: initializeFilterDefaults, // Set up default filter configuration

            // === WORKER LIFECYCLE ===
            startWorker: startWorker, // Initialize background processing worker
            stopWorker: stopWorker, // Terminate worker and cleanup resources
            stopAllWorkers: stopAllWorkers, // Terminate all workers and cleanup resources

            // === STREAM PROCESSING ===
            startStream: startStream, // Create filtered video stream with background effects
            stopStream: stopStream, // Stop video filter stream and cleanup all resources

            // === RUNTIME BACKGROUND CHANGES ===
            onVirtualBackgroundChange: onVirtualBackgroundChange // Handle runtime background changes from UI
        };

        // === CONFIGURATION OBJECTS ===

        // Whether to use a timer-worker instead of requestAnimationFrame for the frame loop.
        // Workers' setInterval is not throttled by Chrome in background tabs, keeping VBG running
        // when the user switches away. Enabled by the vbgTimerWorker Labs/userpair flag.
        var useTimerWorker = false;

        // System configuration for ML-based video filtering
        var systemConfig = {
            model: 'mlkit', // GA default. Overridden at runtime by the 'vbgModel' userpair if set.
            workerURL: '/lib/videobg/volcomix/index.js', // Web Worker script for background processing
            defaultWidth: 640, // Default video processing width in pixels
            defaultHeight: 360, // Default video processing height in pixels
            captureFrameRate: 15, // Target frame rate for video capture and processing
            reduceDetectRate: true, // use the previous rate to reduce detection time, causes background drag in worker-based detection.
            allowedModels: ['mlkit', 'meet', 'meet-lite', 'mediapipe-v2'] // Supported ML model types
        };

        // ML processing and rendering configuration for workers
        var filterConfig = {
            segmentationConfig: {
                backend: 'wasmSimd', // TensorFlow Lite backend: 'wasmSimd' (fastest) or 'wasm' (compatible)
                pipeline: 'webgl2', // Rendering pipeline: 'webgl2' for hardware acceleration
                deferInputResizing: false, // Optimize performance by deferring video resize operations
                debug: false // Enable debug logging in worker
            },
            postProcessingConfig: {
                jointBilateralFilter: { sigmaSpace: 1, sigmaColor: 0.1 }, // Edge-preserving blur parameters
                coverage: [0.5, 0.75], // Segmentation confidence thresholds
                lightWrapping: 0.3, // Background lighting adjustment
                blendMode: 'screen' // Compositing mode for background blending
            }
        };

        // Main filter state for primary video stream processing
        var videoFilter = {
            value: null, // Current filter value ('blur-bg', 'remove-bg', 'change-bg:url', 'none')
            width: 640, // Processing canvas width
            height: 360, // Processing canvas height
            initialized: false, // Whether filter is ready for processing
            canvasOutput: null, // Canvas element for filtered output
            videoInput: null, // Video element receiving camera stream
            canvasStream: null, // MediaStream from output canvas
            videoStream: null, // Original camera MediaStream for cleanup
            averageTime: 50, // Moving average processing time per frame (milliseconds)
            counter: 0, // number of frames processed so far
            framesSent: 0,
            framesReceived: 0,
            firstFrameRendered: false, // track if first frame has been rendered for VBG loading timing
            lastFrameNow: 0, // Frame counters and timing
            animationFrameId: null, // ID for active animation frame loop
            model: 'none', // Start inactive. Will match systemConfig.model when activated
            deviceId: null, // Camera device ID for consistent device selection
            media: null, // Media constraints used for getUserMedia and track setup
            statsHistory: {
                // Performance metrics tracking
                fps: [], // Frames per second history
                resizingTime: [], // Frame resize operation times
                inferenceTime: [], // ML inference times per frame
                postProcessingTime: [], // Post-processing times per frame
                maxAge: 5000, // Maximum age for history entries (5 seconds)
                lastLog: 0 // Timestamp of last performance statistics log
            }
        };

        service.videoFilter = videoFilter;

        // Main background processing worker for ML inference
        var videoWorker = {
            thread: null, // Web Worker instance for background processing
            initialized: false, // Whether worker has loaded ML models and is ready
            queuedBackgroundUpdate: null // Stores background config when worker not yet ready
        };

        // Collection of workers for preview video streams (indexed by stream identifier)
        var previewWorkers = {};

        // Collection of filter states for preview video streams (indexed by stream identifier)
        var previewFilters = {};

        function initialize() {
            logger.debug('initialize', `Starting with default model="${systemConfig.model}"`);

            var volcomixConfig = configService.getValue('volcomixConfig');

            if (volcomixConfig) {
                var container = {
                    videoFilter,
                    systemConfig,
                    segmentationConfig: filterConfig.segmentationConfig,
                    postProcessingConfig: filterConfig.postProcessingConfig
                };
                configService.setConfigObjectFromCSV(container, volcomixConfig);
                logger.log('volcomixConfig updated', `new model="${systemConfig.model}"`);
            }

            logger.info('initialize', `Volcomix initialized - model="${systemConfig.model}", isActive()=${isActive()}`);

            var vbgTimerWorker = configService.getValue('vbgTimerWorker');
            if (vbgTimerWorker && vbgTimerWorker !== 'false') {
                useTimerWorker = true;
                logger.log('initialize', 'vbgTimerWorker enabled: using timer-worker for frame loop');
            }
        }

        // Reset a filter's state to initial values
        function resetFilterState(filter) {
            if (!filter) return;

            // Cancel any ongoing animation
            stopTimerWorker(filter, 'resetFilterState');

            // Reset counters and state
            filter.framesSent = filter.framesReceived = filter.lastFrameNow = 0;
            filter.initialized = false;
            filter.value = null;

            // Clear media elements
            filter.canvasOutput = filter.videoInput = filter.canvasStream = filter.videoStream = null;
        }

        // Update background effect for a filter (main or preview)
        function updateBackgroundEffect(filterValue, identifier) {
            if (!filterValue) return;

            // Get the target filter
            var filter = identifier ? previewFilters[identifier] : videoFilter;
            if (!filter) return;

            // Store the filter value
            filter.value = filterValue;

            // Convert to worker configuration
            var backgroundConfig = convertFilterToBackgroundConfig(filterValue);

            // Handle blob URLs before sending to worker
            if (backgroundConfig.type === 'image' && backgroundConfig.url && backgroundConfig.url.startsWith('blob:')) {
                // Convert blob URL to data URL before sending to worker
                convertBlobToDataUrl(backgroundConfig.url)
                    .then(function (dataUrl) {
                        var updatedConfig = {
                            type: 'image',
                            url: dataUrl
                        };
                        sendWorkerBackgroundUpdate(updatedConfig, identifier);
                    })
                    .catch(function (error) {
                        logger.error('updateBackgroundEffect', 'Failed to convert blob URL to data URL', error);
                        sendWorkerBackgroundUpdate({ type: 'none' }, identifier);
                    });
            } else {
                // Non-blob configs can be sent directly
                sendWorkerBackgroundUpdate(backgroundConfig, identifier);
            }
        }

        // Send background update message to worker
        function sendWorkerBackgroundUpdate(backgroundConfig, identifier) {
            var worker = getWorker(identifier);
            if (!worker || !worker.thread) {
                logger.warn('sendWorkerBackgroundUpdate', 'No worker available for background update');
                return;
            }

            if (!worker.initialized) {
                logger.debug('sendWorkerBackgroundUpdate', 'Worker not initialized, queuing background update');
                // Store the update to apply when worker is ready
                worker.queuedBackgroundUpdate = backgroundConfig;
                return;
            }

            if (!backgroundConfig) {
                logger.warn('sendWorkerBackgroundUpdate', 'Invalid background configuration');
                return;
            }

            try {
                worker.thread.postMessage({
                    type: 'updateBackground',
                    background: backgroundConfig
                });
                logger.debug('sendWorkerBackgroundUpdate', `Background update sent for ${identifier || 'main'}`);
            } catch (error) {
                logger.error('sendWorkerBackgroundUpdate', 'Failed to send background update:', error);
            }
        }

        // Create a fresh worker state object
        function createWorkerState() {
            return {
                thread: null,
                initialized: false,
                queuedBackgroundUpdate: null // Stores background config when worker not yet ready
            };
        }

        // Stop and clean up the timer-worker (and any pending rAF) attached to a filter.
        function stopTimerWorker(filter, callerName) {
            if (filter.timerWorker) {
                logger.log(callerName || 'stopTimerWorker', 'vbgTimerWorker: stopping timer-worker');
                filter.timerWorker.postMessage({ type: 'stop' });
                filter.timerWorker.terminate();
                filter.timerWorker = null;
            }
            if (filter.animationFrameId) {
                $window.cancelAnimationFrame(filter.animationFrameId);
                filter.animationFrameId = null;
            }
        }

        // === INTERNAL FUNCTIONS ===

        // Configure main filter with system defaults
        function initializeFilterDefaults() {
            videoFilter.width = systemConfig.defaultWidth;
            videoFilter.height = systemConfig.defaultHeight;
            videoFilter.model = systemConfig.model;
            logger.debug(
                'initializeFilterDefaults',
                `Filter defaults set - width: ${videoFilter.width}, height: ${videoFilter.height}, model: ${videoFilter.model}`
            );
        }

        // Initialize a new filter with performance tracking capabilities
        function createFilterWithTracking(filter, width, height) {
            filter.width = width || systemConfig.defaultWidth;
            filter.height = height || systemConfig.defaultHeight;
            resetFilterState(filter);
            filter.model = systemConfig.model;
            filter.deviceId = null; // Camera device ID for consistent device selection
            filter.media = null; // Media constraints used for getUserMedia and track setup
            filter.statsHistory = {
                fps: [],
                resizingTime: [],
                inferenceTime: [],
                postProcessingTime: [],
                maxAge: 5000
            };
        }

        // Create or retrieve filter instance for preview stream
        function getFilterByIdentifier(identifier, sourceFilter) {
            if (!identifier) {
                // Main stream - return the main videoFilter
                return videoFilter;
            }

            // Preview stream - create or get preview filter
            if (!previewFilters[identifier]) {
                previewFilters[identifier] = {};
                var newFilter = previewFilters[identifier];

                // Initialize with default values
                createFilterWithTracking(
                    newFilter,
                    sourceFilter ? sourceFilter.width : 640,
                    sourceFilter ? sourceFilter.height : 360
                );

                // Add source filter properties if available
                if (sourceFilter) {
                    if (sourceFilter.value) {
                        updateBackgroundEffect(sourceFilter.value, identifier);
                    }
                }
            } else if (sourceFilter) {
                // Update existing filter dimensions but don't share elements
                var existingFilter = previewFilters[identifier];
                existingFilter.width = sourceFilter.width;
                existingFilter.height = sourceFilter.height;
            }

            return previewFilters[identifier];
        }

        // Check if given model type is supported
        function isSupported(modelType) {
            return systemConfig.allowedModels.includes(modelType);
        }

        // Check if this system is active or not
        // Returns true only if the current systemConfig.model is a Volcomix model
        function isActive() {
            return systemConfig.allowedModels.includes(systemConfig.model);
        }

        // Get current ML model
        function getModel() {
            logger.debug('getModel', `Returning current model: ${systemConfig.model}`);
            return systemConfig.model;
        }

        // Change active ML model and update all workers
        function setModel(newModel) {
            logger.log('setModel', `Model update requested: ${systemConfig.model} => ${newModel}`);

            var oldModel = systemConfig.model;
            var wasActive = isActive();

            // Only update if the new model is in the allowed list OR if it's explicitly being disabled
            if (!systemConfig.allowedModels.includes(newModel)) {
                logger.log(
                    'setModel',
                    `Model "${newModel}" is not in allowedModels, Volcomix will become inactive (was active: ${wasActive})`
                );
                systemConfig.model = newModel; // Still set it to mark as inactive
                videoFilter.model = newModel; // Sync filter model
                if (wasActive) {
                    stopAllWorkers(); // Stop workers since model is no longer supported
                }
                return;
            }

            systemConfig.model = newModel;
            videoFilter.model = newModel; // Sync filter model
            logger.log(
                'setModel',
                `Model successfully updated to: ${newModel}, Volcomix is now ACTIVE (was active: ${wasActive})`
            );

            if (systemConfig.allowedModels.includes(newModel)) {
                // Update existing workers with the new model
                if (oldModel !== newModel) {
                    // Update main worker if it exists
                    if (videoWorker.thread && videoWorker.initialized) {
                        updateWorkerModel(newModel);
                    }

                    // Update all preview workers
                    Object.keys(previewWorkers).forEach(function (identifier) {
                        if (previewWorkers[identifier].thread && previewWorkers[identifier].initialized) {
                            updateWorkerModel(newModel, identifier);
                        }
                    });
                }
            } else {
                stopAllWorkers();
            }
        }

        // Get current status and readiness of worker (main or preview)
        function getWorkerStatus(identifier) {
            var worker = identifier ? previewWorkers[identifier] : videoWorker;
            var status = {
                thread: worker?.thread,
                initialized: worker?.initialized || false,
                exists: !!worker?.thread,
                ready: !!(worker?.thread && worker?.initialized)
            };

            return status;
        }

        // Retrieve worker instance (main worker or specific preview worker)
        function getWorker(identifier) {
            return identifier ? previewWorkers[identifier] : videoWorker;
        }

        // Create canvas and video elements for video processing pipeline
        function createCanvasElements(filter) {
            var canvasOutput = (filter.canvasOutput = $window.document.createElement('canvas'));
            var videoInput = (filter.videoInput = $window.document.createElement('video'));
            setCanvasDimensions(canvasOutput, videoInput, systemConfig.defaultWidth, systemConfig.defaultHeight);
            videoInput.setAttribute('autoplay', true);
            videoInput.setAttribute('muted', true);
        }

        // Set dimensions for canvas and video elements
        function setCanvasDimensions(canvas, video, width, height) {
            canvas.width = video.width = width;
            canvas.height = video.height = height;
        }

        // Apply platform-specific media constraints for optimal processing
        function updateMediaConstraints(media) {
            if (media && media.video) {
                media.video.width = Math.min(media.video.width || 1280, systemConfig.defaultWidth);
                media.video.height = Math.min(media.video.height || 720, systemConfig.defaultHeight);
            }
        }

        // Determine if worker needs to be initialized for given filter value
        function checkWorkerInitialization(value, identifier) {
            var workerStatus = getWorkerStatus(identifier);
            var needsInit = !workerStatus.thread;

            return needsInit;
        }

        // Helper function to safely cleanup MediaStream tracks
        function cleanupStreamTracks(stream) {
            if (stream) {
                try {
                    const tracks = stream.getTracks();
                    tracks.forEach(track => {
                        track.stop();
                        stream.removeTrack(track);
                    });
                    stream = null;
                } catch (e) {
                    logger.error('cleanupStreamTracks', 'Error stopping tracks:', e);
                }
            }
        }

        // Create background effect configuration object
        const typeMapping = {
            blur: { config: { type: 'blur' }, filterValue: 'blur-bg' },
            none: { config: { type: 'none' }, filterValue: 'none' },
            remove: { config: { type: 'none' }, filterValue: 'remove-bg' }
        };

        function createBackgroundConfig(type, url) {
            if (url) {
                return {
                    config: { type: 'image', url: url },
                    filterValue: 'change-bg:' + url
                };
            }

            return typeMapping[type] || null;
        }

        // Convert filter value string to internal background configuration
        function convertFilterToBackgroundConfig(filterValue) {
            if (!filterValue) return { type: 'none' };

            if (filterValue === 'blur-bg') return { type: 'blur' };
            if (filterValue === 'none') return { type: 'none' };
            if (filterValue === 'remove-bg') return { type: 'none' };
            if (filterValue.startsWith('change-bg:')) {
                return { type: 'image', url: filterValue.substr(10) };
            }

            return { type: 'none' };
        }

        // Calculate average value from performance history. Round to 2 decimal places.
        function calculateAverage(array) {
            return array.length === 0
                ? undefined
                : Math.round((array.reduce((s, a) => s + a, 0) / array.length) * 100) / 100;
        }

        // === FILTER INITIALIZATION FUNCTIONS ===

        // Retrieve stored filter value from browser storage
        function getFilterFromStorage() {
            var value = storageService.get('videoFilter');
            if (!value) {
                return null;
            }
            return value;
        }

        function initializeFilterData(identifier, filterData) {
            // Extract filter value from filterData parameter
            var filterValue = filterData?.value || filterData?.filterValue || filterData;

            // If no filter value from parameter, try to get from storage (for main stream)
            if (!filterValue && !identifier) {
                filterValue = getFilterFromStorage();
            }

            // Get the appropriate filter object
            var filter;
            if (identifier) {
                // For preview streams, get or create preview filter
                filter = getFilterByIdentifier(identifier);
            } else {
                // For main stream, use main filter
                filter = videoFilter;
            }

            // Get previous value
            var previous = filter.value || 'none';

            // Set the new filter value if provided
            if (filterValue && filterValue !== 'none') {
                filter.value = filterValue;
            }

            return {
                filterData: filterData,
                filter: filter,
                previous: previous,
                value: filterValue || filter.value || 'none'
            };
        }

        // Create and configure canvas elements for video processing
        function setupCanvases(filter) {
            // Create output canvas elements
            createCanvasElements(filter);

            //// Create canvas stream from output canvas
            //filter.canvasStream = filter.canvasOutput.captureStream(systemConfig.captureFrameRate);
            filter.canvasStream = filter.canvasOutput.captureStream();
        }

        // Configure media constraints and initialize camera access
        function setupMedia(getMediaConstraints, filter) {
            var media = getMediaConstraints('video-filter');

            if (media && media.video) {
                // Use system configuration to overwrite
                media.video.frameRate = Math.min(media.video.frameRate || 30, systemConfig.captureFrameRate);
                updateMediaConstraints(media);

                if (filter.deviceId) {
                    // Use exact deviceId format for consistent device selection
                    media.video.deviceId = { exact: filter.deviceId };
                }
            }

            return {
                media: media,
                deviceId: media?.video?.deviceId?.exact || media?.video?.deviceId
            };
        }

        // Handle successful camera stream initialization
        function handleStreamSuccess(
            stream,
            filter,
            identifier,
            timer,
            completeCallback,
            customizeStreamCallback,
            deviceId
        ) {
            if (!timer) {
                cleanupStreamTracks(stream);
                if (identifier) {
                    stopStream(identifier);
                }
                return;
            }
            // Store reference to original camera stream for proper cleanup
            if (filter.videoStream) {
                logger.warn('handleStreamSuccess', `Replacing existing camera stream for ${identifier || 'main'}`);
                // Clean up the existing camera stream before storing the new one
                cleanupStreamTracks(filter.videoStream);
            }

            filter.videoStream = stream;

            if (!filter.videoInput) {
                logger.error('handleStreamSuccess', 'videoInput not found');
                $rootScope.$broadcast('send-msgflow-event', {
                    name: `device-error`,
                    data: { reason: 'videoFilter', constraints: filter.media, error: 'videoInput not found' }
                });
                if (completeCallback) {
                    completeCallback(identifier, 'video input not found', deviceId);
                }
                if (customizeStreamCallback) {
                    customizeStreamCallback('video input not found');
                }
                $timeout.cancel(timer);
                cleanupStreamTracks(stream);
                return;
            }

            filter.videoInput.srcObject = stream;

            // Handle video metadata loading
            filter.videoInput.addEventListener(
                'loadedmetadata',
                function () {
                    handleVideoMetadata(filter, identifier);
                },
                { once: true }
            );

            $timeout.cancel(timer);

            // Setup video track
            var videoTrack = stream.getVideoTracks()[0];
            setupVideoTrack(videoTrack, filter.media);

            // Complete callbacks
            if (completeCallback) {
                completeCallback(identifier);
            }
            if (customizeStreamCallback) {
                customizeStreamCallback('success', stream);
            }
        }

        // Configure video track properties and constraints
        function setupVideoTrack(videoTrack, media) {
            if (helperService.isMac()) {
                var track1 = videoTrack.clone();
                $timeout(function () {
                    track1.stop();
                }, 2000);

                applyMacConstraints(videoTrack, media).catch(function (error) {
                    logger.error('setupVideoTrack', 'Failed to apply Mac constraints:', error);
                });
            }

            videoTrack.contentHint = 'motion';
            videoTrack.addEventListener('ended', function (event) {
                logger.log('setupVideoTrack', 'Input stream track ended unexpectedly');
                $rootScope.$broadcast('send-msgflow-event', {
                    name: `track-ended`,
                    data: { reason: 'videoFilter', trackId: videoTrack.id, type: 'video-input', src: 'volcomix' }
                });
                $rootScope.$broadcast('active-sfu-track-ended', {
                    track: videoTrack,
                    ignoreMediaState: true
                });
            });
        }

        // Handle camera access errors and fallback scenarios
        function handleStreamError(
            err,
            timer,
            filter,
            completeCallback,
            customizeStreamCallback,
            deviceId,
            identifier,
            media
        ) {
            logger.error('handleStreamError', 'getUserMedia error:', err);
            $timeout.cancel(timer);

            $rootScope.$broadcast('send-msgflow-event', {
                name: `device-error`,
                data: { reason: 'videoFilter', constraints: media, error: err?.name }
            });

            if (filter) {
                // Only clean up our canvas stream in error cases
                if (filter.canvasStream) {
                    cleanupStreamTracks(filter.canvasStream);
                }
                if (filter.videoStream) {
                    cleanupStreamTracks(filter.videoStream);
                }
                filter.canvasStream = filter.videoStream = null;
            }

            if (completeCallback) {
                completeCallback(identifier, err, deviceId);
            }
            if (customizeStreamCallback) {
                customizeStreamCallback('permission error');
            }
        }

        // Initialize video stream with ML-based background filtering
        function startStream(getMediaConstraints, filterData, identifier, completeCallback, customizeStreamCallback) {
            // Early validation - VDI and availability checks are handled by parent videoFilterService

            // Initialize filter data with the passed filterData
            var initData = initializeFilterData(identifier, filterData);
            if (!initData.filter) {
                logger.error('startStream', `No filter available for identifier: ${identifier}`);
                return undefined;
            }

            // If no filter value provided, use 'none' as default to at least start the stream
            if (!initData.value || initData.value === '') {
                initData.value = 'none';
            }

            // Log transition
            logger.log(
                'startStream',
                `${(initData.previous || '').substr(0, 20)} => ${(initData.value || '').substr(0, 20)}${
                    identifier ? ` identifier=${identifier}` : ''
                }`
            );
            if (!identifier) {
                videoFilter.firstFrameRendered = false;
            }

            // Update background effect if changed
            if (initData.previous !== initData.value) {
                updateBackgroundEffect(initData.value, identifier);
            }

            // Initialize worker if needed
            var needsWorkerInit = checkWorkerInitialization(initData.value, identifier);
            if (needsWorkerInit) {
                startWorker(identifier);
            }

            // Handle initialization
            initializeFilterStream(initData.filter, identifier, initData.value);

            // Stream creation or reuse
            if (!initData.filter.canvasStream) {
                return createFilterStream(
                    initData.filter,
                    identifier,
                    getMediaConstraints,
                    completeCallback,
                    customizeStreamCallback
                );
            } else {
                // Existing stream - notify callback
                $timeout(function () {
                    if (customizeStreamCallback) {
                        customizeStreamCallback('stream exists', initData.filter.videoStream);
                    }
                }, 0);
            }

            // Return cloned stream
            var clonedStream = initData.filter.canvasStream.clone();
            logger.log('startStream', `Returning stream:`, clonedStream);
            // Stream returned to caller (videoFilterService) which handles setActiveFeedStream
            return clonedStream;
        }

        // Create new filtered video stream with camera access
        function createFilterStream(
            filter,
            identifier,
            getMediaConstraints,
            completeCallback,
            customizeStreamCallback
        ) {
            // Set loading state for main stream
            if (!identifier) {
                resetFilterState(videoFilter);
                $window.$('#videoroom-videos').attr('videobg', 'loading');
                $rootScope.$broadcast('virtual-background-initialized', false);
            }

            // Setup canvas elements
            setupCanvases(filter);

            // Setup media constraints
            var mediaSetup = setupMedia(getMediaConstraints, filter);
            filter.media = mediaSetup.media; // Store for video track setup

            // Create timeout timer
            var timer = $timeout(function () {
                logger.log('createFilterStream', 'getUserMedia took longer than 10s');
                timer = null;
                if (completeCallback) {
                    completeCallback(identifier, 'timed out', mediaSetup.deviceId);
                }
                if (customizeStreamCallback) {
                    customizeStreamCallback('timed out');
                }
            }, 10000);

            logger.log('createFilterStream', `getUserMedia constraints=${JSON.stringify(mediaSetup.media)}`);

            // Request user media
            $window.navigator?.mediaDevices
                .getUserMedia(mediaSetup.media)
                .then(function (stream) {
                    handleStreamSuccess(
                        stream,
                        filter,
                        identifier,
                        timer,
                        completeCallback,
                        customizeStreamCallback,
                        mediaSetup.deviceId
                    );
                })
                .catch(function (err) {
                    handleStreamError(
                        err,
                        timer,
                        filter,
                        completeCallback,
                        customizeStreamCallback,
                        mediaSetup.deviceId,
                        identifier,
                        mediaSetup.media
                    );
                });

            return filter.canvasStream;
        }

        // Setup filter stream processing with validation and defaults
        function initializeFilterStream(filter, identifier, value) {
            if (!filter) {
                return;
            }

            // Allow 'none' value - we can still create a stream without background effects
            if (!value) {
                value = 'none';
            }

            // Set the filter value and update background
            updateBackgroundEffect(value, identifier);

            // Initialize counter properties if needed
            if (!filter.framesSent && !filter.framesReceived) {
                resetFilterState(filter);
            }

            // Initialize worker if needed (even for 'none' values in previews to support dynamic changes)
            if (!getWorkerStatus(identifier).ready) {
                startWorker(identifier);
            }
        }

        // Helper function to convert blob URLs to data URLs for worker compatibility
        function convertBlobToDataUrl(blobUrl) {
            return fetch(blobUrl)
                .then(function (response) {
                    if (!response.ok) {
                        throw new Error(`Failed to fetch blob: ${response.status}`);
                    }
                    return response.blob();
                })
                .then(function (blob) {
                    return new Promise(function (resolve, reject) {
                        const reader = new FileReader();
                        reader.onload = function () {
                            resolve(reader.result);
                        };
                        reader.onerror = function () {
                            reject(new Error('Failed to read blob as data URL'));
                        };
                        reader.readAsDataURL(blob);
                    });
                })
                .catch(function (error) {
                    logger.error('convertBlobToDataUrl', 'Error converting blob URL:', error);
                    throw error;
                });
        }

        // Helper function to clear canvas
        function clearCanvas(canvas) {
            var ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            return ctx;
        }

        // Extract and normalize background configuration from input data
        function parseBackgroundData(data, identifier) {
            // Extract URL and type from various data formats
            var url = data?.backgroundSrc || data?.url;
            var type = data?.type || data?.backgroundType;
            var backgroundIdentifier = data?.identifier; // This is the background type like 'bg1', 'blur', 'none'

            // Convert to standardized format using helper
            var contextLabel = identifier ? `preview(${identifier})` : 'main';
            var backgroundType = backgroundIdentifier || type;
            var backgroundResult = createBackgroundConfig(backgroundType, url);

            if (!backgroundResult) {
                logger.warn('parseBackgroundData', `Invalid background change data for ${contextLabel}:`, data);
                return null;
            }

            var backgroundConfig = backgroundResult.config;
            var filterValue = backgroundResult.filterValue;

            return { backgroundConfig: backgroundConfig, filterValue: filterValue };
        }

        // Provide fallback background configuration when none specified
        function getDefaultBG(identifier) {
            var currentValue;

            if (identifier) {
                // For preview streams, get from preview filter
                currentValue = previewFilters[identifier]?.value;
            } else {
                // For main stream, get from main filter
                currentValue = videoFilter.value;
            }

            var config = convertFilterToBackgroundConfig(currentValue);

            // Handle blob URL conversion for image backgrounds
            if (config.type === 'image' && config.url && config.url.startsWith('blob:')) {
                return convertBlobToDataUrl(config.url)
                    .then(function (dataUrl) {
                        return { type: 'image', url: dataUrl };
                    })
                    .catch(function (error) {
                        logger.error('getDefaultBG', 'Failed to convert blob URL, falling back to none:', error);
                        return { type: 'none' };
                    });
            }

            return Promise.resolve(config);
        }

        // Create and configure new Web Worker for ML processing
        function initializeWorker(workerURL, identifier, modelType) {
            var worker = getWorker(identifier);

            // Prevent multiple initializations
            if (worker && worker.thread) {
                return;
            }

            // Create worker object if it doesn't exist for preview
            if (!worker) {
                previewWorkers[identifier] = createWorkerState();
                worker = previewWorkers[identifier];
            }

            // Create the worker with error handling
            try {
                worker.thread = new Worker(workerURL);
            } catch (error) {
                logger.error('initializeWorker', `Failed to create Web Worker for ${identifier || 'main'}`, error);
                return;
            }

            worker.initialized = false;

            // Set up message handler
            worker.thread.addEventListener('message', function (event) {
                processWorkerMessage(event.data, worker, identifier);
            });

            // Add error handler
            worker.thread.addEventListener('error', function (error) {
                logger.error('initializeWorker', `Worker error for ${identifier || 'main'}:`, error);
            });

            // Send initialization message - model configs and TFLite paths now handled internally by worker

            // Create base init message
            var initMsg = {
                type: 'init',
                width: videoFilter.width,
                height: videoFilter.height,
                modelType: modelType,
                segmentationConfig: filterConfig.segmentationConfig,
                postProcessingConfig: filterConfig.postProcessingConfig
            };

            getDefaultBG(identifier)
                .then(function (backgroundConfig) {
                    initMsg.background = backgroundConfig;
                    worker.thread.postMessage(initMsg);
                })
                .catch(function (error) {
                    logger.error('initializeWorker', 'Error getting background config:', error);
                    // Fallback to no background
                    initMsg.background = { type: 'none' };
                    worker.thread.postMessage(initMsg);
                });
        }

        // Validate structure and content of worker messages
        const validTypes = [
            'frame',
            'ready',
            'status',
            'error',
            'stats',
            'modelLoaded',
            'updateBackground',
            'updateModel'
        ];

        function isValidMessage(data) {
            return data && validTypes.includes(data.type);
        }

        // Process worker initialization completion and configure ML models
        function handleWorkerReady(data, worker, identifier) {
            // Prevent duplicate ready processing
            if (worker && worker.initialized) {
                return;
            }

            // Mark this specific worker as ready
            if (worker) {
                worker.initialized = true;

                // Apply any pending background updates
                if (worker.queuedBackgroundUpdate) {
                    logger.debug('handleWorkerReady', 'Applying pending background update for worker');
                    sendWorkerBackgroundUpdate(worker.queuedBackgroundUpdate, identifier);
                    worker.queuedBackgroundUpdate = null;
                }
            } else {
                logger.error(
                    'handleWorkerReady',
                    `No worker object found for ${identifier || 'main'} - this should not happen`
                );
                return;
            }

            // For main stream, handle global initialization
            if (!identifier) {
                if (!videoFilter.initialized) {
                    videoFilter.initialized = true;
                    $window.$('#videoroom-videos').removeAttr('videobg');
                    $rootScope.$broadcast('virtual-background-initialized', true);
                }

                // Apply any pending background change for main stream
                if (videoFilter.value) {
                    getDefaultBG()
                        .then(function (backgroundConfig) {
                            sendWorkerBackgroundUpdate(backgroundConfig);
                        })
                        .catch(function (error) {
                            logger.error(
                                'handleWorkerReady',
                                'Error getting background config for main stream:',
                                error
                            );
                        });
                }
            } else {
                // For preview streams, apply their specific background from previewFilters
                if (previewFilters[identifier] && previewFilters[identifier].value) {
                    var previewValue = previewFilters[identifier].value;
                    var previewConfig = convertFilterToBackgroundConfig(previewValue);

                    // Handle blob URL conversion if needed
                    if (previewConfig.type === 'image' && previewConfig.url && previewConfig.url.startsWith('blob:')) {
                        convertBlobToDataUrl(previewConfig.url)
                            .then(function (dataUrl) {
                                previewConfig.url = dataUrl;
                                sendWorkerBackgroundUpdate(previewConfig, identifier);
                            })
                            .catch(function (error) {
                                logger.error('handleWorkerReady', 'Error converting blob URL for preview:', error);
                                sendWorkerBackgroundUpdate({ type: 'none' }, identifier);
                            });
                    } else {
                        sendWorkerBackgroundUpdate(previewConfig, identifier);
                    }
                }
            }
        }

        // Process ML model loading completion
        function handleWorkerModelLoaded(data) {
            logger.info('handleWorkerModelLoaded', 'Worker model loaded successfully');
        }

        // Process worker status change notifications
        function handleWorkerStatus(data) {
            // Status updates - currently no special handling needed
        }

        // Process and log worker error conditions
        function handleWorkerError(data, identifier) {
            logger.error('handleWorkerError', `Worker error for ${identifier || 'main'}: ${data.error}`);

            // Attempt to recover from worker errors
            if (data.error && data.error.includes('model')) {
                logger.warn('Worker model loading failed, attempting to reinitialize worker');
            }

            // For critical errors, still mark as initialized to prevent blocking
            if (
                !identifier &&
                !videoFilter.initialized &&
                (data.error.includes('not supported') || data.error.includes('WebGL') || data.error.includes('WASM'))
            ) {
                videoFilter.initialized = true;
                $window.$('#videoroom-videos').removeAttr('videobg');
                $rootScope.$broadcast('virtual-background-initialized', false); // false indicates error state
            }

            // For preview streams, also mark worker as ready to prevent endless waiting
            if (identifier) {
                var worker = getWorker(identifier);
                if (worker && !worker.initialized) {
                    worker.initialized = true;
                }
            }
        }

        // Process and track ML processing performance metrics
        const statKeys = ['fps', 'resizingTime', 'inferenceTime', 'postProcessingTime'];

        function handleWorkerStats(data) {
            var statsHistory = videoFilter.statsHistory;

            // Add all available stats to their respective arrays
            statKeys.forEach(key => data[key] !== undefined && statsHistory[key].push(data[key]));

            // Check if we have complete timing data and should log averages
            if (!statKeys.map(key => data[key]).includes(undefined, 1)) {
                var now = Date.now();
                if (!statsHistory.lastLog || now - statsHistory.lastLog >= statsHistory.maxAge) {
                    statsHistory.lastLog = now;

                    // Calculate averages and log performance summary
                    var avg = statKeys.map(key => calculateAverage(statsHistory[key]));
                    var avgTotalTime = statKeys
                        .map((key, i) => (i > 0 ? avg[i] : 0))
                        .reduce((s, a) => s + a, 0)
                        .toFixed(2);

                    console.info(
                        `${systemConfig.model} total-time=${avgTotalTime}ms, frame-rate: ${avg[0]}`,
                        `(resizing ${avg[1]}ms, inference ${avg[2]}ms, post-processing ${avg[3]}ms)`
                    );

                    // Clear stats arrays after logging to prevent memory growth
                    statKeys.forEach(key => (statsHistory[key].length = 0));
                }
            }
        }

        // Process completed frame from ML worker and render to canvas
        function handleWorkerFrame(data, worker, identifier) {
            // worker returns processed ImageBitmap directly
            // Use the correct filter object based on identifier
            var currentFilter;
            var targetFilter;

            if (identifier) {
                // For preview streams, use preview filter from previewFilters collection
                targetFilter = previewFilters[identifier];
                currentFilter = targetFilter; // Use preview filter
            } else {
                // For main stream, use main filter, fallback to traditional
                targetFilter = videoFilter;
                currentFilter = targetFilter.canvasOutput ? targetFilter : videoFilter;
            }

            // Handle frame counting like worker-test.html
            // Update filter frame counters
            if (targetFilter) {
                targetFilter.framesReceived++;
                // Sanity check - matches HTML implementation
                if (targetFilter.framesReceived > targetFilter.framesSent) {
                    targetFilter.framesReceived = targetFilter.framesSent;
                }
            }

            // Maintain compatibility with legacy frame counters
            if (currentFilter && currentFilter !== targetFilter) {
                // Initialize frame counters if not present - ensure they're numbers
                if (typeof currentFilter.framesReceived !== 'number') {
                    currentFilter.framesReceived = 0;
                }
                if (typeof currentFilter.framesSent !== 'number') {
                    currentFilter.framesSent = 0;
                }

                currentFilter.framesReceived++;
                // Sanity check - matches HTML implementation
                if (currentFilter.framesReceived > currentFilter.framesSent) {
                    currentFilter.framesReceived = currentFilter.framesSent;
                }
            }

            if (currentFilter && currentFilter.canvasOutput && data.imageBitmap) {
                var outputCtx = clearCanvas(currentFilter.canvasOutput);

                outputCtx.drawImage(
                    data.imageBitmap,
                    0,
                    0,
                    currentFilter.canvasOutput.width,
                    currentFilter.canvasOutput.height
                );

                // Track VBG loading end when first frame is rendered (main stream only, once)
                if (!identifier && !videoFilter.firstFrameRendered) {
                    videoFilter.firstFrameRendered = true;
                    $rootScope.$broadcast('vbg-loading', 'end');
                }

                // Clean up the ImageBitmap
                data.imageBitmap.close();
            }

            if (data.processingTime && data.processingTime < 400) {
                var filterFactor = 0.1;
                videoFilter.averageTime =
                    data.processingTime * filterFactor + videoFilter.averageTime * (1 - filterFactor);
            }
            videoFilter.counter += 1;
            if (videoFilter.counter % 32 === 0) {
                console.info(systemConfig.model, 'tf-time=', Math.round(videoFilter.averageTime));
            }
        }

        // Route and handle incoming messages from ML worker
        function processWorkerMessage(data, worker, identifier) {
            // Validate message first
            if (!isValidMessage(data)) {
                logger.error(
                    'processWorkerMessage',
                    `Invalid message received for ${identifier || 'main'}:`,
                    JSON.stringify(data)
                );
                return;
            }

            switch (data.type) {
                case 'ready':
                    handleWorkerReady(data, worker, identifier);
                    break;
                case 'frame':
                    handleWorkerFrame(data, worker, identifier);
                    break;
                case 'error':
                    handleWorkerError(data, identifier);
                    break;
                case 'stats':
                    handleWorkerStats(data);
                    break;
                case 'modelLoaded':
                    handleWorkerModelLoaded(data);
                    break;
                case 'status':
                    handleWorkerStatus(data);
                    break;
                default:
                    logger.warn(
                        'processWorkerMessage',
                        `Unknown message type ${data.type} for ${identifier || 'main'}`
                    );
            }
        }

        // Terminate worker and clean up processing resources
        function stopWorker(identifier) {
            logger.info('stopWorker', `Stopping Volcomix worker for ${identifier || 'main'}`);

            // Get worker instance
            const worker = getWorker(identifier);
            if (!worker) {
                logger.warn('stopWorker', 'No worker found for cleanup');
                return;
            }

            try {
                // Stop worker thread
                if (worker.thread) {
                    try {
                        // Send dispose message before terminating
                        worker.thread.postMessage({ type: 'dispose' });
                        worker.thread.terminate();
                    } catch (error) {
                        logger.error('stopWorker', `Error terminating worker: ${error.message}`);
                    }
                    worker.thread = null;
                }

                // Remove from preview workers or reset main worker
                if (identifier && previewWorkers[identifier]) {
                    delete previewWorkers[identifier];
                } else if (!identifier) {
                    // Reset main worker to clean state
                    Object.assign(videoWorker, createWorkerState());
                }

                logger.info('stopWorker', `Worker cleanup completed for ${identifier || 'main'}`);
            } catch (error) {
                logger.error('stopWorker', `Fatal error during worker cleanup: ${error.message}`);
            }
        }

        // Stop all workers and reset all filters
        function stopAllWorkers() {
            logger.info('stopAllWorkers', 'Stopping all Volcomix workers and resetting filters');

            try {
                // Stop all preview workers first
                Object.keys(previewWorkers).forEach(identifier => {
                    stopWorker(identifier);
                });

                // Stop main worker last
                stopWorker();

                // Clean up all preview filters
                Object.keys(previewFilters).forEach(identifier => {
                    if (previewFilters[identifier]) {
                        delete previewFilters[identifier];
                    }
                });

                // Clean up all stored camera stream references from filters
                Object.keys(previewFilters).forEach(identifier => {
                    const filter = previewFilters[identifier];
                    if (filter && filter.videoStream) {
                        logger.debug('stopAllWorkers', `Cleaning up stored camera stream for ${identifier}`);
                        cleanupStreamTracks(filter.videoStream);
                        filter.videoStream = null;
                    }
                });

                // Clean up main filter camera stream
                if (videoFilter.videoStream) {
                    logger.debug('stopAllWorkers', 'Cleaning up main camera stream');
                    cleanupStreamTracks(videoFilter.videoStream);
                    videoFilter.videoStream = null;
                }

                // Reset main filter to defaults
                resetFilterState(videoFilter);
                initializeFilterDefaults();

                // Reset UI state
                $window.$('#videoroom-videos').removeAttr('videobg');
                $rootScope.$broadcast('virtual-background-initialized', false);

                logger.info('stopAllWorkers', 'All Volcomix workers stopped and filters reset');
            } catch (error) {
                logger.error('stopAllWorkers', `Error stopping workers and resetting filters: ${error.message}`);
            }
        }

        // Switch ML model type in active worker (worker uses internal MODEL_FILE_CONFIGS for paths)
        function updateWorkerModel(modelType, identifier) {
            var worker = getWorker(identifier);
            if (!worker || !worker.thread) {
                return;
            }

            // Validate model type (worker will resolve path internally from MODEL_FILE_CONFIGS)
            if (!isSupported(modelType)) {
                logger.error('updateWorkerModel', 'Unknown model type:', modelType);
                return;
            }

            try {
                // Worker uses internal MODEL_FILE_CONFIGS to resolve model path
                worker.thread.postMessage({
                    type: 'updateModel',
                    modelType: modelType
                });
            } catch (error) {
                logger.error('updateWorkerModel', `Error updating model for ${identifier || 'main'}`, error);
            }
        }

        // Begin continuous video frame processing loop
        function startProcessingLoop(filter, identifier) {
            // Always start the loop like the original - don't check worker readiness here
            if (!filter || !filter.videoInput || !filter.canvasOutput) {
                return false;
            }

            // Don't require worker to be ready at start - like the original file
            // The processing loop will check worker readiness inside the frame loop

            // Snapshot the module-level flag so fallback paths only affect this loop instance,
            // not other filters or future invocations.
            var loopUsesTimerWorker = useTimerWorker;

            // Initialize frame counters - always reset for clean start
            filter.framesSent = filter.framesReceived = filter.lastFrameNow = 0;
            filter.lastFrameNow = performance.now();

            // Create processing function matching original pattern
            function processFrame() {
                var now = performance.now();

                if (!filter.videoInput || filter.videoInput.readyState !== 4) {
                    // Video not ready, try again later — only re-schedule rAF in rAF mode.
                    // In timer-worker mode the timer will call processFrame again automatically.
                    if (!loopUsesTimerWorker) {
                        filter.animationFrameId = $window.requestAnimationFrame(processFrame);
                    }
                    return;
                }

                // Reset frame counters if too much time has passed (> 1 second) - matches original
                if (now > filter.lastFrameNow + 1000) {
                    filter.framesReceived = filter.framesSent;
                }

                // Check worker inside loop - like original file
                var currentWorker = getWorker(identifier);

                // Send frames with proper throttling - for all filter values including 'none'
                var canSendFrame =
                    filter.framesReceived >= filter.framesSent || filter.framesSent - filter.framesReceived < 2;

                // Only send frames if worker is ready and throttling allows
                if (currentWorker && currentWorker.thread && currentWorker.initialized && canSendFrame) {
                    // do not send image to worker more often than capture framerate.
                    if (
                        !systemConfig.reduceDetectRate ||
                        now - filter.lastFrameNow >= 1000 / systemConfig.captureFrameRate
                    ) {
                        filter.lastFrameNow = now;
                        sendImageToWorker(filter.videoInput, identifier);
                    }
                }

                // Continue processing loop — only re-schedule rAF in rAF mode.
                // In timer-worker mode the next tick will come from the worker automatically.
                if (!loopUsesTimerWorker) {
                    filter.animationFrameId = $window.requestAnimationFrame(processFrame);
                }
            }

            if (loopUsesTimerWorker) {
                // Use a timer-worker instead of rAF so the loop keeps running in background tabs.
                // Compute interval from captureFrameRate, fall back to 15fps default, clamp to sane range.
                var rate = systemConfig.captureFrameRate;
                var timerInterval = rate > 0 ? Math.round(1000 / rate) : Math.round(1000 / 15);
                timerInterval = Math.min(Math.max(timerInterval, 16), 1000); // clamp: 16ms–1000ms
                if (!isFinite(timerInterval) || timerInterval <= 0) {
                    logger.warn('startProcessingLoop', 'vbgTimerWorker: invalid timerInterval, falling back to rAF');
                    loopUsesTimerWorker = false;
                    processFrame();
                    return true;
                }

                // Stop any existing timer-worker or rAF loop before creating a new one (leak prevention).
                stopTimerWorker(filter, 'startProcessingLoop');

                logger.log(
                    'startProcessingLoop',
                    `vbgTimerWorker: starting timer-worker at interval=${timerInterval}ms, identifier=${
                        identifier || 'main'
                    }`
                );
                try {
                    filter.timerWorker = new $window.Worker('/lib/videobg/timer-worker.js');
                    filter.timerWorker.onerror = function (err) {
                        logger.warn('startProcessingLoop', 'vbgTimerWorker: worker error, falling back to rAF:', err);
                        stopTimerWorker(filter, 'startProcessingLoop');
                        loopUsesTimerWorker = false;
                        processFrame();
                    };
                    filter.timerWorker.onmessage = function (e) {
                        if (e.data && e.data.type === 'tick') {
                            // Drop tick if previous frame is still being processed (queued-tick guard).
                            if (filter.tickPending) {
                                return;
                            }
                            filter.tickPending = true;
                            processFrame();
                            filter.tickPending = false;
                        }
                    };
                    filter.timerWorker.postMessage({ type: 'start', interval: timerInterval });
                } catch (e) {
                    logger.warn(
                        'startProcessingLoop',
                        'vbgTimerWorker: failed to create worker, falling back to rAF:',
                        e
                    );
                    filter.timerWorker = null;
                    loopUsesTimerWorker = false;
                    processFrame();
                }
            } else {
                // Start the processing loop (always start, like original)
                processFrame();
            }

            $window.$('#videoroom-videos').removeAttr('videobg');
            $rootScope.$broadcast('virtual-background-initialized', true);

            return true;
        }

        // Capture and send current video frame for ML processing
        function sendImageToWorker(videoElement, identifier) {
            var worker = getWorker(identifier);
            var filter = identifier ? previewFilters[identifier] : videoFilter;

            if (!worker || !worker.thread || !filter) {
                return;
            }

            try {
                // Create ImageBitmap from video for high performance
                $window
                    .createImageBitmap(videoElement)
                    .then(function (imageBitmap) {
                        // Send to worker with validation
                        var frameMessage = {
                            type: 'frame',
                            imageBitmap: imageBitmap,
                            timestamp: performance.now()
                        };

                        worker.thread.postMessage(frameMessage, [imageBitmap]);

                        // Update counters - increment framesSent only after successful send
                        filter.framesSent++;
                    })
                    .catch(function (error) {
                        logger.error('sendImageToWorker', 'Error creating ImageBitmap', error);
                    });
            } catch (error) {
                logger.error('sendImageToWorker', 'Error sending frame to worker', error);
            }
        }

        // Process video stream metadata and configure processing
        function handleVideoMetadata(filter, identifier) {
            // Use video's natural dimensions for processing quality - matches worker-test.html
            var naturalWidth = filter.videoInput.videoWidth || 1280;
            var naturalHeight = filter.videoInput.videoHeight || 720;

            // Update output canvas to use natural video dimensions for better quality
            filter.canvasOutput.width = naturalWidth;
            filter.canvasOutput.height = naturalHeight;

            //// Recreate stream with updated dimensions
            //filter.canvasStream = filter.canvasOutput.captureStream(systemConfig.captureFrameRate);

            // Start the processing loop for all filter values (including 'none' for passthrough)
            startProcessingLoop(filter, identifier);
        }

        // Apply macOS-specific video constraints for optimal processing
        function applyMacConstraints(videoTrack, media) {
            var constraints = Object.assign({}, videoTrack.getConstraints(), {
                width: Math.min(media.video.width || 1280, systemConfig.defaultWidth),
                height: Math.min(media.video.height || 720, systemConfig.defaultHeight)
            });
            return videoTrack.applyConstraints(constraints);
        }

        // Stop video filter stream and perform comprehensive cleanup
        function stopStream(identifier) {
            const filter = identifier ? previewFilters[identifier] : videoFilter;
            if (!filter) {
                logger.warn('stopStream', 'No filter found for cleanup');
                return;
            }

            logger.info('stopStream', `Stopping stream for ${identifier || 'main'}`);

            try {
                // Cancel animation frame
                stopTimerWorker(filter, 'stopStream');

                // Clean up video input element
                if (filter.videoInput) {
                    filter.videoInput.pause();
                    filter.videoInput.srcObject = null;
                    filter.videoInput = null;
                }

                // Clean up stored camera stream
                if (filter.videoStream) {
                    logger.debug('stopStream', `Cleaning up stored camera stream for ${identifier || 'main'}`);

                    cleanupStreamTracks(filter.videoStream);

                    // Clear the stored reference
                    filter.videoStream = null;
                    logger.debug('stopStream', `Removed camera stream reference for ${identifier || 'main'}`);
                }

                // Clean up canvas
                if (filter.canvasOutput) {
                    clearCanvas(filter.canvasOutput);
                }

                // Clean up canvas stream
                if (filter.canvasStream) {
                    cleanupStreamTracks(filter.canvasStream);
                }

                // Clean up video stream
                if (filter.videoStream) {
                    cleanupStreamTracks(filter.videoStream);
                }

                // Clear all references in one line
                filter.canvasOutput = filter.canvasStream = filter.videoStream = null;

                // Reset filter state
                resetFilterState(filter);

                logger.info('stopStream', `Stream cleanup completed for ${identifier || 'main'}`);
            } catch (error) {
                logger.error('stopStream', `Error during stream cleanup: ${error.message}`);
            }
        }

        // Initialize worker instance for video processing (main or preview stream)
        function startWorker(identifier) {
            // For preview workers, use separate initialization
            if (identifier) {
                if (!previewWorkers[identifier]?.thread) {
                    initializeWorker(systemConfig.workerURL, identifier, systemConfig.model);
                }
                return true;
            }

            // Prevent multiple initializations for the same model (main worker)
            if (videoWorker.thread) {
                return true;
            }

            // Initialize main worker
            initializeWorker(systemConfig.workerURL, null, systemConfig.model);
            return true;
        }

        // Handle runtime virtual background changes from UI
        function onVirtualBackgroundChange(data) {
            var identifier = data?.previewIdentifier || data?.identifier;
            var result = parseBackgroundData(data, identifier);

            if (!result) {
                logger.warn('onVirtualBackgroundChange', 'Invalid background change data:', data);
                return;
            }
            updateBackgroundEffect(result.filterValue, identifier);
        }

        return service;
    }
]);
